/**
 * Matching for typed vocab answers in the Study session.
 *
 * Kept separate from the quiz grader (`apps/api/src/quiz/grading.ts`) on
 * purpose: quiz grading is intentionally strict about some axes (A0
 * orthography quizzes test the diacritic itself), whereas vocab recall
 * should forgive everything that isn't the meaning. This lives in
 * `packages/shared` so the web (client-side grading in `Study.tsx`) and
 * the API can share one definition if server-side grading is ever added.
 */

/**
 * Fold a typed vocab answer to its comparable core:
 *  - lowercase — «Платок» === «платок»
 *  - strip diacritics via NFD + combining-mark removal — «citește» ===
 *    «citeste», «ёж» === «еж» (matches the quiz grader's tolerance;
 *    right for A1/A2 recall)
 *  - drop parenthetical qualifiers «(…)» — they disambiguate a sense but
 *    aren't part of what the learner should have to type, so
 *    «платок (головной)» collapses to «платок»
 *  - collapse internal whitespace and trim
 */
export function normalizeVocabAnswer(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/\([^)]*\)/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * True when `typed` matches any accepted translation after
 * normalization. Empty input never matches (guards against an accepted
 * value that normalizes to empty).
 */
export function matchVocabAnswer(typed: string, accepted: string[]): boolean {
  const normalized = normalizeVocabAnswer(typed);
  if (!normalized) return false;
  return accepted.some((a) => normalizeVocabAnswer(a) === normalized);
}
